/**
 * DashboardLayout Component - Dashboard pages layout
 */

import React, { useState } from 'react';
import { Outlet, NavLink, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@hooks/useAuth';
import './DashboardLayout.scss';

const DashboardLayout = ({ type = 'couple' }) => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  // Navigation items based on user type
  const coupleNavItems = [
    { to: '/dashboard', icon: 'bi-house', label: t('nav.dashboard'), end: true },
    { to: '/dashboard/my-wedding', icon: 'bi-heart', label: t('nav.myWedding') },
    { to: '/dashboard/guests', icon: 'bi-people', label: t('nav.guestList') },
    { to: '/dashboard/website', icon: 'bi-globe', label: t('nav.websiteBuilder') },
    { to: '/dashboard/budget', icon: 'bi-wallet2', label: t('nav.budget') },
    { to: '/dashboard/checklist', icon: 'bi-check2-square', label: t('nav.checklist') },
    { to: '/dashboard/bookings', icon: 'bi-calendar-check', label: t('nav.bookings') },
    { to: '/dashboard/messages', icon: 'bi-chat', label: t('nav.messages') },
    { to: '/dashboard/ai-concierge', icon: 'bi-stars', label: 'AI Concierge' },
    { to: '/dashboard/ceremony-templates', icon: 'bi-building', label: 'Ceremony Templates' },
    { to: '/dashboard/qr-invites', icon: 'bi-qr-code', label: 'QR Invites' },
    { to: '/dashboard/planning-boards', icon: 'bi-kanban', label: 'Planning Boards' },
    { to: '/dashboard/vendor-compare', icon: 'bi-bar-chart', label: 'Vendor Compare' },
    { to: '/dashboard/settings', icon: 'bi-gear', label: t('nav.settings') },
  ];

  const vendorNavItems = [
    { to: '/vendor', icon: 'bi-house', label: t('nav.vendorDashboard'), end: true },
    { to: '/vendor/profile', icon: 'bi-person-badge', label: t('nav.vendorProfile') },
    { to: '/vendor/bookings', icon: 'bi-calendar-check', label: t('nav.vendorBookings') },
    { to: '/vendor/calendar', icon: 'bi-calendar3', label: t('nav.vendorCalendar') },
    { to: '/vendor/messages', icon: 'bi-chat', label: t('nav.vendorMessages') },
    { to: '/vendor/quotes', icon: 'bi-receipt', label: 'Quote Requests' },
    { to: '/vendor/settings', icon: 'bi-gear', label: t('nav.vendorSettings') },
  ];

  const adminNavItems = [
    { to: '/admin', icon: 'bi-shield-lock', label: 'Dashboard', end: true },
    { to: '/admin/users', icon: 'bi-people', label: 'Users' },
    { to: '/admin/vendors', icon: 'bi-shop', label: 'Vendors' },
    { to: '/admin/venues', icon: 'bi-building', label: 'Venues' },
    { to: '/admin/bookings', icon: 'bi-calendar-check', label: 'Bookings' },
    { to: '/admin/revenue', icon: 'bi-graph-up', label: 'Revenue' },
    { to: '/admin/categories', icon: 'bi-tags', label: 'Categories' },
    { to: '/admin/reviews', icon: 'bi-chat-square-text', label: 'Reviews' },
    { to: '/admin/audit', icon: 'bi-journal-text', label: 'Audit Log' },
    { to: '/admin/settings', icon: 'bi-gear', label: 'Settings' },
    { to: '/admin/docs', icon: 'bi-book', label: 'Live Docs' },
  ];

  const venueOwnerNavItems = [
    { to: '/venue', icon: 'bi-house', label: 'Dashboard', end: true },
    { to: '/venue/profile', icon: 'bi-building', label: 'Venue Profile' },
    { to: '/venue/photos', icon: 'bi-images', label: 'Photos' },
    { to: '/venue/amenities', icon: 'bi-check2-square', label: 'Amenities' },
    { to: '/venue/availability', icon: 'bi-calendar-x', label: 'Availability' },
    { to: '/venue/bookings', icon: 'bi-calendar-check', label: 'Bookings' },
    { to: '/venue/reviews', icon: 'bi-star', label: 'Reviews' },
  ];

  const coordinatorNavItems = [
    { to: '/coordinator', icon: 'bi-clipboard2-check', label: 'Dashboard', end: true },
    { to: '/coordinator/weddings', icon: 'bi-heart', label: 'My Weddings' },
    { to: '/coordinator/calendar', icon: 'bi-calendar3', label: 'Calendar' },
    { to: '/coordinator/vendors', icon: 'bi-shop', label: 'Vendor Network' },
    { to: '/coordinator/settings', icon: 'bi-gear', label: 'Settings' },
  ];

  const navItems = type === 'vendor' ? vendorNavItems : type === 'admin' ? adminNavItems : type === 'venue_owner' ? venueOwnerNavItems : type === 'coordinator' ? coordinatorNavItems : coupleNavItems;

  return (
    <div className="dashboard">
      {/* Sidebar */}
      <aside className={`dashboard__sidebar ${sidebarOpen ? 'open' : ''}`}>
        {/* Logo */}
        <div className="dashboard__logo">
          <Link to="/">
            <img src="/logo-128.png" alt="MyWeddingWiz.com" style={{ height: 38, marginRight: 6, verticalAlign: 'middle' }} />
            W<span>O360</span>
          </Link>
        </div>

        {/* Navigation */}
        <nav className="dashboard__nav">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `dashboard__nav-item ${isActive ? 'active' : ''}`
              }
              onClick={() => setSidebarOpen(false)}
            >
              <i className={`bi ${item.icon}`}></i>
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        {/* User Info & Logout */}
        <div className="dashboard__user">
          <div className="dashboard__user-info">
            <div className="dashboard__user-avatar">
              {user?.avatar ? (
                <img src={user.avatar} alt={user.first_name} />
              ) : (
                <i className="bi bi-person"></i>
              )}
            </div>
            <div className="dashboard__user-details">
              <span className="dashboard__user-name">
                {user?.first_name} {user?.last_name}
              </span>
              <span className="dashboard__user-email">{user?.email}</span>
            </div>
          </div>
          <button onClick={handleLogout} className="dashboard__logout">
            <i className="bi bi-box-arrow-right"></i>
            <span>{t('common.logout')}</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="dashboard__main">
        {/* Mobile Header */}
        <header className="dashboard__header">
          <button
            className="dashboard__menu-btn"
            onClick={() => setSidebarOpen(!sidebarOpen)}
          >
            <i className={`bi bi-${sidebarOpen ? 'x' : 'list'}`}></i>
          </button>
          <Link to="/" className="dashboard__header-logo">
            <img src="/logo-128.png" alt="MyWeddingWiz.com" style={{ height: 34, marginRight: 4, verticalAlign: 'middle' }} />
            MyWeddingWiz
          </Link>
          <div className="dashboard__header-user">
            <div className="dashboard__user-avatar">
              {user?.avatar ? (
                <img src={user.avatar} alt={user.first_name} />
              ) : (
                <i className="bi bi-person"></i>
              )}
            </div>
          </div>
        </header>

        {/* Content */}
        <div className="dashboard__content">
          <Outlet />
        </div>
      </main>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="dashboard__overlay"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default DashboardLayout;
